import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";
import {
    getHasMinAmountValidationError,
    getHasMinMaxValidationError,
    getMaxAmountValidationError,
    getMinAmountValidationError,
} from "@qgiv/donation-form";
import {
    selectAllDonationDetails,
    updateDisplayMinMaxErrorMessage,
} from "../../../../../redux/slices/donationDetailsSlice";
import { selectAllDonationSettings } from "../../../../../redux/slices/donationSettingsSlice";
import { selectAllFormSettings } from "../../../../../redux/slices/formSettingsSlice";
import MinMaxErrorMessage from "../../../../common/MinMaxErrorMessage";

/**
 * @typedef {import("./../../../../common/MinMaxErrorMessage/minMaxErrorMessageTypes").MessageProps} MessageProps
 * @typedef {import("./../../../../common/MinMaxErrorMessage/minMaxErrorMessageTypes").UseUpdateShowMessageProps} UseUpdateShowMessageProps
 */

const ConnectedMinMaxErrorMessage = () => {
    const dispatch = useDispatch();
    const donationDetails = useSelector(selectAllDonationDetails);
    const donationSettings = useSelector(selectAllDonationSettings);
    const formikContext = useFormikContext();
    const formSettings = useSelector(selectAllFormSettings);
    const { displayMessage = false, giftDetails = {} } = donationDetails;
    const { maxAmt = 0, minAmt = 0 } = donationSettings;
    const { submitCount = 0 } = formikContext;
    const { currency = {} } = formSettings;
    const { subtotal = 0 } = giftDetails;

    // Get data from helper functions
    const hasMinAmountValidationError = getHasMinAmountValidationError(
        minAmt,
        subtotal,
    );
    const minAmountValidationError = getMinAmountValidationError(
        minAmt,
        currency,
    );
    const maxAmtValidationError = getMaxAmountValidationError(maxAmt, currency);
    const hasMinMaxValidationError = getHasMinMaxValidationError({
        maxAmt,
        minAmt,
        subtotal,
    });

    // Flags that will be used in the props that are passed down
    const hasAttemptedSubmission = submitCount > 0;
    const message = hasMinAmountValidationError
        ? minAmountValidationError
        : maxAmtValidationError;

    // Used to show and hide message once it has been displayed
    const handleHideMessage = () => {
        dispatch(updateDisplayMinMaxErrorMessage(false));
    };

    const handleShowMessage = () => {
        dispatch(updateDisplayMinMaxErrorMessage(true));
    };

    // Consolidate props into objects that will be passed down
    /** @type {MessageProps} */
    const messageProps = {
        displayMessage,
        message,
    };
    /** @type {UseUpdateShowMessageProps} */
    const useUpdateShowMessageProps = {
        handleHideMessage,
        handleShowMessage,
        hasAttemptedSubmission,
        hasMinMaxValidationError,
        subtotal,
    };

    return (
        <MinMaxErrorMessage
            messageProps={messageProps}
            useUpdateShowMessageProps={useUpdateShowMessageProps}
        />
    );
};

export default ConnectedMinMaxErrorMessage;
